import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import { confirmDialog } from '../components/ConfirmDialog';

type ProductOption = {
  id: string;
  name: string;
  category: string;
};

type CostAdjustment = {
  id: string;
  scope: 'global' | 'product';
  applyOn?: 'cost' | 'sale';
  productId?: string | null;
  productName?: string | null;
  mode: 'percent' | 'manual';
  percentValue?: number | null;
  manualCost?: number | null;
  filterBrand?: string | null;
  filterModel?: string | null;
  filterCategory?: string | null;
  filterSupplierName?: string | null;
  filterMeasureWidth?: number | null;
  filterMeasureRatio?: number | null;
  filterRim?: number | null;
  isActive: number;
  startsAt?: string | null;
  endsAt?: string | null;
  note?: string | null;
  updatedAt?: string | null;
};

type CostAdjustmentFormState = {
  scope: 'global' | 'product';
  applyOn: 'cost' | 'sale';
  productId: string;
  mode: 'percent' | 'manual';
  percentValue: string;
  manualCost: string;
  filterBrand: string;
  filterModel: string;
  filterCategory: string;
  filterSupplierName: string;
  filterMeasureWidth: string;
  filterMeasureRatio: string;
  filterRim: string;
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  note: string;
};

type DistinctMeta = {
  brands: string[];
  modelsByBrand: Record<string, string[]>;
  measureWidths: number[];
  measureRatios: number[];
  rims: number[];
  suppliers: string[];
  categories: string[];
};

const EMPTY_META: DistinctMeta = {
  brands: [],
  modelsByBrand: {},
  measureWidths: [],
  measureRatios: [],
  rims: [],
  suppliers: [],
  categories: []
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const ProductCostAdjustments: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adjustments, setAdjustments] = useState<CostAdjustment[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [meta, setMeta] = useState<DistinctMeta>(EMPTY_META);
  const [form, setForm] = useState<CostAdjustmentFormState>({
    scope: 'global',
    applyOn: 'cost',
    productId: '',
    mode: 'percent',
    percentValue: '10',
    manualCost: '',
    filterBrand: '',
    filterModel: '',
    filterCategory: '',
    filterSupplierName: '',
    filterMeasureWidth: '',
    filterMeasureRatio: '',
    filterRim: '',
    isActive: true,
    startsAt: '',
    endsAt: '',
    note: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [adjustmentRes, productRes, metaRes] = await Promise.all([
        axios.get('/api/products/cost-adjustments'),
        axios.get('/api/products', { params: { limit: 5000, page: 1 } }),
        axios.get('/api/products/distinct-meta')
      ]);
      setAdjustments(adjustmentRes.data?.adjustments || []);
      setProducts(
        (productRes.data?.products || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          category: p.category
        }))
      );
      setMeta({
        brands: metaRes.data?.brands || [],
        modelsByBrand: metaRes.data?.modelsByBrand || {},
        measureWidths: metaRes.data?.measureWidths || [],
        measureRatios: metaRes.data?.measureRatios || [],
        rims: metaRes.data?.rims || [],
        suppliers: metaRes.data?.suppliers || [],
        categories: metaRes.data?.categories || []
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudieron cargar las ofertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredProducts = useMemo(
    () => products.filter((p) => p.category !== 'servicio'),
    [products]
  );

  const resetForm = () => {
    setEditingId(null);
    setForm({
      scope: 'global',
      applyOn: 'cost',
      productId: '',
      mode: 'percent',
      percentValue: '10',
      manualCost: '',
      filterBrand: '',
      filterModel: '',
      filterCategory: '',
      filterSupplierName: '',
      filterMeasureWidth: '',
      filterMeasureRatio: '',
      filterRim: '',
      isActive: true,
      startsAt: '',
      endsAt: '',
      note: ''
    });
  };

  const handleEdit = (adjustment: CostAdjustment) => {
    setEditingId(adjustment.id);
    setForm({
      scope: adjustment.scope,
      applyOn: adjustment.applyOn === 'sale' ? 'sale' : 'cost',
      productId: adjustment.productId || '',
      mode: adjustment.mode,
      percentValue: adjustment.percentValue != null ? String(adjustment.percentValue) : '0',
      manualCost: adjustment.manualCost != null ? String(adjustment.manualCost) : '',
      filterBrand: adjustment.filterBrand || '',
      filterModel: adjustment.filterModel || '',
      filterCategory: adjustment.filterCategory || '',
      filterSupplierName: adjustment.filterSupplierName || '',
      filterMeasureWidth: adjustment.filterMeasureWidth != null ? String(adjustment.filterMeasureWidth) : '',
      filterMeasureRatio: adjustment.filterMeasureRatio != null ? String(adjustment.filterMeasureRatio) : '',
      filterRim: adjustment.filterRim != null ? String(adjustment.filterRim) : '',
      isActive: Number(adjustment.isActive || 0) === 1,
      startsAt: toDateInputValue(adjustment.startsAt),
      endsAt: toDateInputValue(adjustment.endsAt),
      note: adjustment.note || ''
    });
  };

  const handleSubmit = async () => {
    if (form.scope === 'product' && !form.productId) {
      toast.error('Selecciona un producto para la oferta especifica');
      return;
    }
    if (form.mode === 'percent' && Number(form.percentValue || 0) <= 0) {
      toast.error('El porcentaje debe ser mayor a 0');
      return;
    }
    if (form.mode === 'manual' && Number(form.manualCost || 0) <= 0) {
      toast.error('El costo manual debe ser mayor a 0');
      return;
    }
    if (form.startsAt && form.endsAt && new Date(form.endsAt).getTime() < new Date(form.startsAt).getTime()) {
      toast.error('La fecha de fin no puede ser menor a la de inicio');
      return;
    }

    const toIntOrNull = (v: string) => {
      const t = String(v || '').trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    };
    const toTextOrNull = (v: string) => {
      const t = String(v || '').trim();
      return t || null;
    };

    const payload: any = {
      scope: form.scope,
      applyOn: form.applyOn,
      productId: form.scope === 'product' ? form.productId : null,
      mode: form.mode,
      percentValue: form.mode === 'percent' ? Number(form.percentValue || 0) : null,
      manualCost: form.mode === 'manual' ? Number(form.manualCost || 0) : null,
      isActive: form.isActive,
      startsAt: form.startsAt || null,
      endsAt: form.endsAt || null,
      note: form.note.trim() || null
    };

    // Filtros: solo se mandan si scope es global
    if (form.scope === 'global') {
      payload.filterBrand = toTextOrNull(form.filterBrand);
      payload.filterModel = toTextOrNull(form.filterModel);
      payload.filterCategory = toTextOrNull(form.filterCategory);
      payload.filterSupplierName = toTextOrNull(form.filterSupplierName);
      payload.filterMeasureWidth = toIntOrNull(form.filterMeasureWidth);
      payload.filterMeasureRatio = toIntOrNull(form.filterMeasureRatio);
      payload.filterRim = toIntOrNull(form.filterRim);
    }

    try {
      setSaving(true);
      if (editingId) {
        await axios.put(`/api/products/cost-adjustments/${editingId}`, payload);
        toast.success('Oferta actualizada');
      } else {
        await axios.post('/api/products/cost-adjustments', payload);
        toast.success('Oferta creada');
      }
      await fetchData();
      resetForm();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo guardar la oferta');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await axios.patch(`/api/products/cost-adjustments/${id}/toggle`);
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo cambiar el estado');
    }
  };

  const handleDelete = async (id: string) => {
    if (!await confirmDialog('Eliminar esta oferta?')) return;
    try {
      await axios.delete(`/api/products/cost-adjustments/${id}`);
      await fetchData();
      if (editingId === id) resetForm();
      toast.success('Oferta eliminada');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Ofertas</h2>
        <Button variant="outline" onClick={() => navigate('/products')}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Volver
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Editar oferta' : 'Nueva oferta'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Alcance</Label>
              <Select value={form.scope} onValueChange={(value: 'global' | 'product') => setForm((prev) => ({ ...prev, scope: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global</SelectItem>
                  <SelectItem value="product">Item especifico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Modo de ajuste</Label>
              <Select value={form.mode} onValueChange={(value: 'percent' | 'manual') => setForm((prev) => ({ ...prev, mode: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">% de descuento</SelectItem>
                  <SelectItem value="manual">Costo manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Aplicar sobre</Label>
              <Select value={form.applyOn} onValueChange={(value: 'cost' | 'sale') => setForm((prev) => ({ ...prev, applyOn: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost">Costo</SelectItem>
                  <SelectItem value="sale">Precio de lista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.scope === 'product' && (
              <div className="space-y-2 md:col-span-2">
                <Label>Producto</Label>
                <Select value={form.productId} onValueChange={(value) => setForm((prev) => ({ ...prev, productId: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un producto" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProducts.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.scope === 'global' && (
              <div className="space-y-3 md:col-span-2 rounded-md border border-dashed p-3">
                <div className="text-xs text-muted-foreground">
                  Filtros (opcional). Si dejas todo en blanco, la oferta aplica a TODOS los productos.
                  Cualquier campo lleno actua como AND.
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Marca</Label>
                    <Select
                      value={form.filterBrand || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({
                        ...prev,
                        filterBrand: value === '__all__' ? '' : value,
                        // Limpiar modelo si cambia la marca
                        filterModel: value === '__all__' || value !== prev.filterBrand ? '' : prev.filterModel
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todas</SelectItem>
                        {meta.brands.map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Modelo</Label>
                    <Select
                      value={form.filterModel || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, filterModel: value === '__all__' ? '' : value }))}
                      disabled={!form.filterBrand}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={form.filterBrand ? 'Todos' : 'Elegi marca primero'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todos</SelectItem>
                        {(meta.modelsByBrand[String(form.filterBrand).toUpperCase()] || []).map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Categoria</Label>
                    <Select
                      value={form.filterCategory || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, filterCategory: value === '__all__' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todas</SelectItem>
                        {meta.categories.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ancho</Label>
                    <Select
                      value={form.filterMeasureWidth || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, filterMeasureWidth: value === '__all__' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Cualquiera" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Cualquiera</SelectItem>
                        {meta.measureWidths.map((w) => (
                          <SelectItem key={w} value={String(w)}>{w}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Perfil</Label>
                    <Select
                      value={form.filterMeasureRatio || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, filterMeasureRatio: value === '__all__' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Cualquiera" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Cualquiera</SelectItem>
                        {meta.measureRatios.map((r) => (
                          <SelectItem key={r} value={String(r)}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rodado</Label>
                    <Select
                      value={form.filterRim || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, filterRim: value === '__all__' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Cualquiera" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Cualquiera</SelectItem>
                        {meta.rims.map((r) => (
                          <SelectItem key={r} value={String(r)}>R{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 md:col-span-3">
                    <Label className="text-xs">Proveedor vigente</Label>
                    <Select
                      value={form.filterSupplierName || '__all__'}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, filterSupplierName: value === '__all__' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Cualquiera" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Cualquiera</SelectItem>
                        {meta.suppliers.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            {form.mode === 'percent' ? (
              <div className="space-y-2">
                <Label>Descuento %</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.percentValue}
                  onChange={(e) => setForm((prev) => ({ ...prev, percentValue: e.target.value }))}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{form.applyOn === 'sale' ? 'Precio manual' : 'Costo manual'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.manualCost}
                  onChange={(e) => setForm((prev) => ({ ...prev, manualCost: e.target.value }))}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Fecha inicio</Label>
              <DatePicker
                value={form.startsAt}
                onChange={(v) => setForm((prev) => ({ ...prev, startsAt: v }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha fin (opcional)</Label>
              <DatePicker
                value={form.endsAt}
                onChange={(v) => setForm((prev) => ({ ...prev, endsAt: v }))}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Nota (opcional)</Label>
              <Input
                value={form.note}
                onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Ej: Oferta proveedor marzo"
              />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                checked={form.isActive}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: Boolean(checked) }))}
              />
              <span className="text-sm">Activo</span>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            {editingId && (
              <Button variant="outline" onClick={resetForm} disabled={saving}>
                Cancelar edicion
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Guardando...' : (editingId ? 'Actualizar oferta' : 'Crear oferta')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ofertas cargadas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando...</div>
          ) : adjustments.length === 0 ? (
            <div className="text-sm text-muted-foreground">No hay ofertas configuradas.</div>
          ) : (
            adjustments.map((adj) => (
              <div key={adj.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={Number(adj.isActive || 0) === 1 ? 'default' : 'secondary'}>
                      {Number(adj.isActive || 0) === 1 ? 'Activo' : 'Inactivo'}
                    </Badge>
                    <Badge variant="outline">
                      {adj.scope === 'global' ? 'Global' : `Item: ${adj.productName || 'Sin nombre'}`}
                    </Badge>
                    <Badge variant="outline">
                      {adj.applyOn === 'sale' ? 'Sobre precio de lista' : 'Sobre costo'}
                    </Badge>
                    <Badge variant="outline">
                      {adj.mode === 'percent'
                        ? `${Number(adj.percentValue || 0).toFixed(2)}% desc.`
                        : `${adj.applyOn === 'sale' ? 'Precio manual' : 'Costo manual'} $${Number(adj.manualCost || 0).toFixed(2)}`}
                    </Badge>
                    {adj.filterBrand && <Badge variant="outline">Marca: {adj.filterBrand}</Badge>}
                    {adj.filterModel && <Badge variant="outline">Modelo: {adj.filterModel}</Badge>}
                    {adj.filterCategory && <Badge variant="outline">Cat: {adj.filterCategory}</Badge>}
                    {adj.filterMeasureWidth != null && <Badge variant="outline">Ancho: {adj.filterMeasureWidth}</Badge>}
                    {adj.filterMeasureRatio != null && <Badge variant="outline">Perfil: {adj.filterMeasureRatio}</Badge>}
                    {adj.filterRim != null && <Badge variant="outline">Rodado: R{adj.filterRim}</Badge>}
                    {adj.filterSupplierName && <Badge variant="outline">Prov: {adj.filterSupplierName}</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleToggle(adj.id)}>
                      {Number(adj.isActive || 0) === 1 ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button size="icon" variant="outline" onClick={() => handleEdit(adj)}>
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" onClick={() => handleDelete(adj.id)}>
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Vigencia: {adj.startsAt ? new Date(adj.startsAt).toLocaleDateString() : 'Inmediata'} - {adj.endsAt ? new Date(adj.endsAt).toLocaleDateString() : 'Sin fin'}
                </div>
                {adj.note && <div className="mt-1 text-xs text-muted-foreground">Nota: {adj.note}</div>}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductCostAdjustments;
